import React, { useState, useContext, useCallback } from 'react';
import { TextInput, Button, Alert } from '@mantine/core';
import { DocumentReference, updateDoc, Timestamp, arrayUnion } from 'firebase/firestore';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { MyUserDataContext } from '../contexts';
import { Service, UpdatedDataType } from '../types';
import BigNumber from 'bignumber.js';

interface AddInitialAmountProps {
    serviceRef: DocumentReference;
    service: Service;
    invoiceIndex: number;
}

const MIN_REASON_LENGTH = 5;

const AddInitialAmount: React.FC<AddInitialAmountProps> = ({
    serviceRef,
    service,
    invoiceIndex,
}) => {
    const [updating, setUpdating] = useState(false);
    const [fieldValue, setFieldValue] = useState<string>(
        String(service.transferInvoices?.[invoiceIndex]?.initialAmount ?? '')
    );
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<{ fieldValue?: string; reason?: string }>({});
    const userData = useContext(MyUserDataContext);

    const validateAmount = useCallback((value: string): string | undefined => {
        const trimmed = value.trim();
        if (!trimmed) return 'Initial amount is required';
        const num = Number(trimmed);
        if (isNaN(num) || !Number.isFinite(num)) return 'Must be a valid number';
        if (num < 0) return 'Must be 0 or greater';
        return undefined;
    }, []);

    const validateReason = useCallback((value: string): string | undefined => {
        const trimmed = value.trim();
        if (!trimmed) return 'Reason is required';
        if (trimmed.length < MIN_REASON_LENGTH) return `Reason must be at least ${MIN_REASON_LENGTH} characters`;
        return undefined;
    }, []);

    const isFormValid = useCallback((): boolean => {
        const num = Number(fieldValue.trim());
        return Number.isFinite(num) && num >= 0 && reason.trim().length >= MIN_REASON_LENGTH;
    }, [fieldValue, reason]);

    const handleAmountChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setFieldValue(value);
        const error = validateAmount(value);
        if (!error) setErrors(prev => ({ ...prev, fieldValue: undefined }));
    }, [validateAmount]);

    const handleReasonChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setReason(value);
        if (value.trim().length >= MIN_REASON_LENGTH) setErrors(prev => ({ ...prev, reason: undefined }));
    }, []);

    const handleSubmit = useCallback(async () => {
        const amountError = validateAmount(fieldValue);
        const reasonError = validateReason(reason);
        setErrors({ fieldValue: amountError, reason: reasonError });
        if (amountError || reasonError) return;

        setUpdating(true);
        try {
            const numericValue = Number(fieldValue.trim());
            const initialFieldValue = service.transferInvoices?.[invoiceIndex]?.initialAmount ?? '';
            const initialAmount = new BigNumber(numericValue);
            const tradingCommission = new BigNumber(service.portfolioOpeningRequest?.trading_commission ?? 0);
            const tradingCommissionAmount = initialAmount.times(tradingCommission);
            const amount = initialAmount.plus(tradingCommissionAmount);

            const newEntry: UpdatedDataType = {
                updated_by: userData.user?.email || 'unknown',
                updated_on: Timestamp.now(),
                updated_field_name: [
                    'initialAmount#' + invoiceIndex,
                    'tradingCommissionAmount#' + invoiceIndex,
                    'amount#' + invoiceIndex,
                ],
                updated_fields: [{
                    initialAmount: numericValue,
                    tradingCommissionAmount: tradingCommissionAmount.toNumber(),
                    amount: amount.toNumber(),
                }],
                initial_value: [{
                    initialAmount: String(initialFieldValue),
                    tradingCommissionAmount: service.transferInvoices?.[invoiceIndex]?.tradingCommissionAmount ?? '',
                    amount: service.transferInvoices?.[invoiceIndex]?.amount ?? '',
                }],
                reason: reason.trim(),
            };

            const updatedInvoices = [...(service.transferInvoices ?? [])];
            updatedInvoices[invoiceIndex] = {
                ...updatedInvoices[invoiceIndex],
                initialAmount: numericValue,
                tradingCommissionAmount: tradingCommissionAmount.toNumber(),
                amount: amount.toNumber(),
            };

            await updateDoc(serviceRef, {
                transferInvoices: updatedInvoices,
                updated_data: arrayUnion(newEntry),
            });

            notifications.show({
                title: 'Success!',
                message: 'Initial amount updated successfully',
                color: 'green',
                icon: <IconCheck size={16} />,
            });

            setFieldValue('');
            setReason('');
            setErrors({});
            modals.closeAll();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error occurred';
            notifications.show({
                title: 'Error',
                message: `Failed to update initial amount: ${message}`,
                color: 'red',
                icon: <IconAlertCircle size={16} />,
            });
        } finally {
            setUpdating(false);
        }
    }, [fieldValue, reason, validateAmount, validateReason, service, invoiceIndex, serviceRef, userData]);

    return (
        <>
            <TextInput
                label="Initial Amount"
                data-autofocus
                value={fieldValue}
                onChange={handleAmountChange}
                onBlur={() => {
                    const error = validateAmount(fieldValue);
                    if (error) setErrors(prev => ({ ...prev, fieldValue: error }));
                }}
                error={errors.fieldValue}
                placeholder="e.g. 10000 or 0"
                required
            />

            <TextInput
                label="Reason for Update"
                value={reason}
                onChange={handleReasonChange}
                onBlur={() => {
                    const error = validateReason(reason);
                    if (error) setErrors(prev => ({ ...prev, reason: error }));
                }}
                error={errors.reason}
                placeholder={`Provide a reason (min ${MIN_REASON_LENGTH} characters)...`}
                required
            />

            <Button
                loading={updating}
                fullWidth
                disabled={updating || !isFormValid()}
                onClick={handleSubmit}
                mt="md"
            >
                Submit
            </Button>

            {(errors.fieldValue || errors.reason) && (
                <Alert icon={<IconAlertCircle size={16} />} title="Fix the following:" color="red" mt="sm">
                    {errors.fieldValue && <div>• {errors.fieldValue}</div>}
                    {errors.reason && <div>• {errors.reason}</div>}
                </Alert>
            )}
        </>
    );
};

export default AddInitialAmount;
